'use client';

import React, { useMemo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { generateAvatarFallback } from '@/utils/generateAvatarFallback';
import { LoadingSpinner } from '@/components/Icons';
import { Search, ChevronDown, X } from 'lucide-react';
import { formatNumber } from '@/utils/coins/formatNumber';
import { Coins } from 'lucide-react';
import Link from 'next/link';
import { sitePath } from '@/utils/links/safeHref';
import { isUsdQuoted } from '@/utils/coins/quote';
import { isBubbleSymbol } from '@/utils/coins/bubble';

/**
 * H1 of a symbol page: «قیمت دلار آمریکا امروز», «قیمت بیت کوین امروز»; bubbles «حباب سکه امامی امروز».
 * The <title> says the same, and that is what people search for; the bare name did not.
 */
export const symbolHeading = (displayName: string, symbol?: string): string => {
    const name = displayName.trim();
    if (!name) return '';
    if (isBubbleSymbol(symbol)) {
        return name.startsWith('حباب') ? `${name} امروز` : `حباب ${name} امروز`;
    }
    return name.startsWith('قیمت') ? `${name} امروز` : `قیمت ${name} امروز`;
};

export interface Coin {
    name: string;
    symbol: string;
    iconUrl: string;
    name_fa?: string;
    slug?: string;
    price?: number | { price_buy?: number; ir_price_buy?: number; usdt_price?: number };
    usdtPrice?: number;
    tomanPrice?: number;
    type?: string;
    /** Gold/currency quoted in dollars (the world gold ounce) */
    price_usd?: number;
    quote_currency?: string;
}

interface SymbolModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentSymbol: string;
    symbolType?: 'crypto' | 'coin' | 'gold' | 'currency' | 'forex';
}

export const SymbolSelectorModal: React.FC<SymbolModalProps> = ({
    isOpen,
    onClose,
    currentSymbol,
    symbolType = 'crypto',
}) => {
    const [coins, setCoins] = useState<Coin[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isMounted, setIsMounted] = useState<boolean>(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const fetchCoins = useMemo(
        () =>
            debounce(async (currentPage: number, currentSearchTerm: string) => {
                setIsLoading(true);
                try {
                    const response = await axios.get(
                        `/api/coin/currencies/?page=${currentPage}&search=${encodeURIComponent(
                            currentSearchTerm,
                        )}&type=${symbolType}`,
                    );

                    const rawData = response.data;
                    let fetchedModels: any[] = [];

                    if (Array.isArray(rawData)) {
                        fetchedModels = rawData;
                    } else if (rawData?.data?.models && Array.isArray(rawData.data.models)) {
                        fetchedModels = rawData.data.models;
                    } else if (rawData?.data && Array.isArray(rawData.data)) {
                        fetchedModels = rawData.data;
                    } else if (rawData?.models && Array.isArray(rawData.models)) {
                        fetchedModels = rawData.models;
                    }

                    const formatted: Coin[] = fetchedModels.map((item: any) => {
                        const sym = item.symbol || '';
                        const priceObj = item.price;
                        let usdtPrice = 0;
                        let tomanPrice = 0;

                        if (typeof priceObj === 'number') {
                            tomanPrice = priceObj;
                            usdtPrice = item.usdt_price || item.usdtPrice || item.price_usd || 0;
                        } else if (typeof priceObj === 'object' && priceObj !== null) {
                            usdtPrice = parseFloat(priceObj.price_buy || '0');
                            tomanPrice = parseFloat(priceObj.ir_price_buy || '0');
                        } else {
                            usdtPrice = Number(item.price || item.usdt_price || 0);
                            tomanPrice = Number(item.tomanPrice || item.price_in_toman || 0);
                        }

                        return {
                            name: item.name || '',
                            name_fa: item.name_fa || item.nameFa || '',
                            symbol: sym,
                            iconUrl: item.iconUrl || item.icon_url || '',
                            slug: (sym || item.slug || '').toLowerCase(),
                            price: priceObj,
                            usdtPrice,
                            tomanPrice,
                            type: item.type,
                            price_usd: item.price_usd,
                            quote_currency: item.quote_currency,
                        };
                    });

                    if (currentPage === 1) {
                        setCoins(formatted);
                    } else {
                        setCoins((prev) => [...prev, ...formatted]);
                    }

                    setHasMore(formatted.length >= 10);
                } catch (err) {
                    console.error('Failed to fetch coins:', err);
                    setHasMore(false);
                } finally {
                    setIsLoading(false);
                }
            }, 300),
        [symbolType],
    );

    useEffect(() => {
        if (isOpen) {
            fetchCoins(page, searchTerm);
        }
    }, [isOpen, page, searchTerm, fetchCoins]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setPage(1);
        setHasMore(true);
    };
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 40 && hasMore && !isLoading) {
            setPage((prev) => prev + 1);
        }
    };

    if (!isOpen || !isMounted || typeof document === 'undefined') return null;

    const modalMarkup = (
        <div
            className='fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-200'
            onClick={onClose}
        >
            {/* Desktop View: Centered Dialog (sm:flex) | Mobile View: Bottom Drawer (sm:hidden) */}
            <div
                className='w-full sm:max-w-md bg-white dark:bg-primary-active border-t sm:border border-gray-100 dark:border-white/15 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom sm:zoom-in-95 duration-250 flex flex-col max-h-[85vh]'
                onClick={(e) => e.stopPropagation()}
            >
                {/* Mobile Bottom Sheet Pull Handle */}
                <div className='sm:hidden pt-3 pb-1 flex justify-center'>
                    <div className='w-12 h-1 rounded-full bg-gray-300 dark:bg-white/20' />
                </div>

                {/* Modal Header */}
                <div className='flex items-center justify-between p-4 border-b border-gray-100 dark:border-white/10'>
                    <h3 className='font-bold text-sm sm:text-base text-gray-900 dark:text-white'>
                        انتخاب و جستجوی نماد
                    </h3>
                    <button
                        type='button'
                        onClick={onClose}
                        className='p-1.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors'
                    >
                        <X className='size-5' />
                    </button>
                </div>

                {/* Search Bar */}
                <div className='p-3 border-b border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/5'>
                    <div className='relative'>
                        <Search className='absolute right-3 top-1/2 -translate-y-1/2 size-4 text-gray-400 dark:text-gray-500' />
                        <input
                            type='text'
                            placeholder='جستجوی نام یا نماد...'
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className='w-full pr-9 pl-8 py-2.5 text-xs sm:text-sm bg-white dark:bg-primary-dark border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 shadow-xs'
                        />
                        {searchTerm && (
                            <button
                                type='button'
                                onClick={() => {
                                    setSearchTerm('');
                                    setPage(1);
                                }}
                                className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                            >
                                <X className='size-3.5' />
                            </button>
                        )}
                    </div>
                </div>

                {/* Scrollable Symbol List */}
                <div
                    onScroll={handleScroll}
                    className='max-h-[380px] overflow-y-auto p-2 space-y-1 no-scrollbar'
                >
                    {coins.map((coin, index) => {
                        const isCurrent = coin.symbol.toLowerCase() === currentSymbol.toLowerCase();
                        const coinName = coin.name_fa || coin.name || '';
                        const symLower = coin.symbol.toLowerCase();
                        const isStableCoin =
                            coin.type === 'stableCoin' ||
                            symLower === 'usdt' ||
                            symLower === 'usdc';
                        // Toman only, except the dollar-quoted gold ounce (dollars + toman like a coin)
                        const isFiatOrGold =
                            (symbolType === 'gold' || symbolType === 'currency') &&
                            !isUsdQuoted(coin);

                        return (
                            <Link
                                href={sitePath('symbol', coin.symbol) ?? '#'}
                                key={`${coin.symbol}-${index}`}
                                className={`flex items-center justify-between p-3 sm:p-2.5 rounded-xl text-xs sm:text-sm cursor-pointer transition-all duration-150 active:bg-gray-200 dark:active:bg-white/10 ${
                                    isCurrent
                                        ? 'bg-blue-50 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 font-bold'
                                        : 'hover:bg-gray-100 dark:hover:bg-white/5 text-gray-700 dark:text-gray-200'
                                }`}
                            >
                                <div className='flex items-center gap-3 overflow-hidden'>
                                    <Avatar className='size-7 sm:size-7 flex-shrink-0 border border-gray-100 dark:border-white/10'>
                                        <AvatarImage src={coin.iconUrl} alt={coinName} />
                                        <AvatarFallback className='text-xs font-bold'>
                                            {generateAvatarFallback(coinName)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className='flex flex-col overflow-hidden text-right'>
                                        <span className='truncate font-medium max-w-[120px] sm:max-w-[170px]'>
                                            {coinName}
                                        </span>
                                        <span className='text-[10px] font-mono text-gray-400 dark:text-gray-500 uppercase font-normal'>
                                            {coin.symbol}
                                        </span>
                                    </div>
                                </div>

                                <div className='flex items-center gap-2 flex-shrink-0'>
                                    {/* Price display */}
                                    <div className='flex flex-col items-start text-xs' dir='ltr'>
                                        {isStableCoin || isFiatOrGold ? (
                                            coin.tomanPrice && coin.tomanPrice > 0 ? (
                                                <span className='font-yekanBakh font-bold dark:text-white text-gray-900'>
                                                    {formatNumber(
                                                        coin.tomanPrice,
                                                        false,
                                                        true,
                                                        'TMN',
                                                        true,
                                                    )}{' '}
                                                    تومان
                                                </span>
                                            ) : null
                                        ) : (
                                            <>
                                                {coin.usdtPrice && coin.usdtPrice > 0 ? (
                                                    <span className='font-yekanBakh font-bold dark:text-white text-gray-900'>
                                                        $
                                                        {formatNumber(
                                                            coin.usdtPrice,
                                                            false,
                                                            true,
                                                            'USD',
                                                            true,
                                                        )}
                                                    </span>
                                                ) : null}
                                                {coin.tomanPrice && coin.tomanPrice > 0 ? (
                                                    <span className='text-xxs text-gray-400 dark:text-gray-500 font-yekanBakh'>
                                                        {formatNumber(
                                                            coin.tomanPrice,
                                                            false,
                                                            true,
                                                            'TMN',
                                                            true,
                                                        )}{' '}
                                                        تومان
                                                    </span>
                                                ) : null}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        );
                    })}

                    {isLoading && (
                        <div className='py-4 text-center'>
                            <LoadingSpinner className='mx-auto size-5 animate-spin text-blue-500' />
                        </div>
                    )}

                    {!isLoading && coins.length === 0 && (
                        <div className='py-8 text-center text-xs text-gray-400 dark:text-gray-500'>
                            هیچ نمادی یافت نشد.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modalMarkup, document.body);
};

interface SingleCoinTitleProps {
    rank: number;
    symbol: string;
    nameFa?: string;
    iconUrl: string;
    name: string;
    currentSlug: string;
    symbolType?: 'crypto' | 'coin' | 'gold' | 'currency' | 'forex';
}

const CoinTitle: React.FC<SingleCoinTitleProps> = ({
    iconUrl,
    name,
    nameFa,
    symbol,
    symbolType = 'crypto',
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const displayName = nameFa || name || '';
    const heading = symbolHeading(displayName, symbol);

    return (
        <>
            <header
                onClick={() => setIsOpen(true)}
                className='flex items-center gap-2.5 sm:gap-3.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-2xl bg-gray-100/80 dark:bg-white/5 hover:bg-gray-200/80 dark:hover:bg-white/10 border border-gray-200/60 dark:border-white/10 transition-all duration-200 active:scale-95 text-right group cursor-pointer'
            >
                {symbolType === 'gold' ? (
                    <div
                        className={
                            'size-5 lg:size-8 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-500 dark:text-amber-400 dark:bg-amber-400/20 flex items-center justify-center shrink-0'
                        }
                    >
                        <Coins className='size-3.5 lg:size-5' />
                    </div>
                ) : (
                    <Avatar className='size-7 sm:size-9 lg:size-11 border border-gray-200 dark:border-white/10 shadow-xs flex-shrink-0'>
                        <AvatarImage
                            className='object-cover'
                            src={iconUrl}
                            alt={`لوگوی ${displayName}`}
                        />
                        <AvatarFallback className='text-xs sm:text-sm font-bold'>
                            {generateAvatarFallback(displayName)}
                        </AvatarFallback>
                    </Avatar>
                )}

                <div className='flex flex-col text-right justify-center'>
                    <div className='flex items-center gap-1 sm:gap-1.5'>
                        <h1 className='text-lg max-lg:text-sm max-lg:font-bold text-gray-900 dark:text-white leading-tight max-w-[150px] sm:max-w-[340px] max-sm:line-clamp-2 sm:text-nowrap'>
                            {heading || displayName}
                        </h1>
                        <ChevronDown className='size-3.5 sm:size-4 lg:size-5 shrink-0 text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors' />
                    </div>

                    {symbol && symbolType === 'crypto' && (
                        <span className='text-xxs lg:text-xs font-mono text-gray-400 dark:text-gray-500 uppercase tracking-wide'>
                            {symbol}
                        </span>
                    )}
                </div>
            </header>

            <SymbolSelectorModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                currentSymbol={symbol}
                symbolType={symbolType}
            />
        </>
    );
};

export default CoinTitle;
